/**
 * Password policy — single source of truth for password rules (AL-470)
 *
 * Every password rule shown to a user, every submit gate and every complexity
 * error message must derive from this module. Before AL-470 the rules lived in
 * three places that disagreed: a config-driven check used only by the reset
 * flow, a hardcoded 8-character check on the new-password challenge, and a
 * hardcoded on-screen checklist that demanded a special character.
 *
 * The live C1 Cognito pool (arcaml-live-c1, ap-southeast-2_KoyBewcxx) requires
 * 8+ characters with uppercase, lowercase and numbers, and does NOT require a
 * symbol. A rule shown but not enforced is worse than no rule at all: the user
 * sees a failure against a password Cognito would have accepted.
 */

import type { BackendConfig } from '../types/config';
import type { PasswordStrengthRules } from '../components/auth/types';

export type PasswordPolicy = BackendConfig['auth'];

/** A single requirement, as shown to the user. */
export interface PasswordRequirement {
	key: keyof PasswordStrengthRules;
	text: string;
	met: boolean;
}

export interface PasswordCheck {
	/** Per-rule results. Rules the pool does not enforce are always true. */
	rules: PasswordStrengthRules;
	/** True when every enforced rule passes. */
	isValid: boolean;
	/**
	 * Only the requirements the pool actually enforces, in display order.
	 * Rules that are switched off are omitted rather than shown pre-ticked, so
	 * the user is never asked for something that does not matter.
	 */
	requirements: PasswordRequirement[];
}

const SPECIAL_CHAR_PATTERN = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

/**
 * Evaluate a password against the configured policy.
 *
 * Pure and synchronous — safe to call on every keystroke.
 */
export function checkPassword(password: string, policy: PasswordPolicy): PasswordCheck {
	const rules: PasswordStrengthRules = {
		minLength: password.length >= policy.passwordMinLength,
		hasUppercase: !policy.requireUppercase || /[A-Z]/.test(password),
		hasLowercase: !policy.requireLowercase || /[a-z]/.test(password),
		hasNumber: !policy.requireNumbers || /\d/.test(password),
		hasSpecialChar: !policy.requireSpecialChar || SPECIAL_CHAR_PATTERN.test(password),
	};

	// Built from the policy so the wording cannot drift from the enforcement.
	const requirements: PasswordRequirement[] = [
		{
			key: 'minLength' as const,
			text: `At least ${policy.passwordMinLength} characters`,
			met: rules.minLength,
			enforced: true,
		},
		{
			key: 'hasUppercase' as const,
			text: 'One uppercase letter (A-Z)',
			met: rules.hasUppercase,
			enforced: policy.requireUppercase,
		},
		{
			key: 'hasLowercase' as const,
			text: 'One lowercase letter (a-z)',
			met: rules.hasLowercase,
			enforced: policy.requireLowercase,
		},
		{
			key: 'hasNumber' as const,
			text: 'One number (0-9)',
			met: rules.hasNumber,
			enforced: policy.requireNumbers,
		},
		{
			key: 'hasSpecialChar' as const,
			text: 'One special character (!@#$%^&*)',
			met: rules.hasSpecialChar,
			enforced: policy.requireSpecialChar,
		},
	]
		.filter((r) => r.enforced)
		.map(({ key, text, met }) => ({ key, text, met }));

	return {
		rules,
		isValid: Object.values(rules).every(Boolean),
		requirements,
	};
}

/**
 * One sentence describing the policy, for use in error messages.
 *
 * Generated rather than hardcoded so it cannot claim a symbol is required when
 * the pool does not require one.
 */
export function describePasswordPolicy(policy: PasswordPolicy): string {
	const parts: string[] = [];
	if (policy.requireUppercase) parts.push('an uppercase letter');
	if (policy.requireLowercase) parts.push('a lowercase letter');
	if (policy.requireNumbers) parts.push('a number');
	if (policy.requireSpecialChar) parts.push('a special character');

	const prefix = `Your password must be at least ${policy.passwordMinLength} characters`;
	if (parts.length === 0) return `${prefix}.`;

	const list =
		parts.length === 1
			? parts[0]
			: `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
	return `${prefix} and include ${list}.`;
}
